import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { tierGradientBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'

const CardContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: ${rem(8)};
  overflow: hidden;
  box-shadow:
    0 ${rem(1)} ${rem(1.5)} rgba(0, 0, 0, 0.07),
    0 ${rem(1.5)} ${rem(5)} rgba(0, 0, 0, 0.05);
`

const CardHeader = styled.div`
  padding: ${rem(10)} ${rem(12)};
  ${tierGradientBackgroundStyle}

  /* Platinum uses a custom gradient instead of gradientPrimaryBackground */
  &.tier-platinum {
    background: linear-gradient(
      45deg,
      ${props => props.theme.palette.product.loyalty.platinum.darkForeground} 0.73%,
      ${props => props.theme.palette.product.loyalty.platinum.lightBackground} 45.77%,
      ${props => props.theme.palette.product.loyalty.platinum.darkForeground} 74.49%
    );
  }
`

const HeaderText = styled.span`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(14)};
  font-weight: ${props => props.theme.font.primary.weight.bold};
  line-height: ${rem(18)};
  ${tierNormalContrastColorStyle}

  /* Platinum uses darkContrast instead of normalContrast */
  &.tier-platinum {
    color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
  }
`

const CardBody = styled.div`
  background: ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(16)};
`

const BodyTextStyled = styled.p`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(12)};
  font-weight: ${props => props.theme.font.primary.weight.regular};
  line-height: ${rem(16)};
  /* Using bronze contrast instead of neural one to get the same color as the figma design */
  color: ${props => props.theme.palette.neutral.default.one};
  margin: 0;
`

const BoldText = styled.span`
  font-weight: ${props => props.theme.font.primary.weight.superBold};
`

interface Props {
  /**
   * The tier to display (next tier for unlock, current tier for retain)
   */
  tier: App.LoyaltyTier
  /**
   * Whether this is for retaining current tier (true) or unlocking next tier (false)
   */
  isRetain?: boolean
  creditsRemaining: number
  nightsRemaining: number
  expiryDate?: string
}

function LoyaltyDrawerTierCard({
  tier,
  isRetain = false,
  creditsRemaining,
  nightsRemaining,
  expiryDate,
}: Props) {
  const intl = useIntl()
  const tierName = getLoyaltyTierDisplayName(tier)

  const hasCredits = creditsRemaining > 0
  const hasNights = nightsRemaining > 0

  const toRetainText = intl.formatMessage(
    {
      id: 'c1N1FNxcje',
      defaultMessage: 'To retain {tierName}',
      description: 'Header text for tier retention requirement',
    },
    { tierName },
  )
  const toUnlockText = intl.formatMessage(
    {
      id: 'lC0tMO84xO',
      defaultMessage: 'To unlock {tierName}',
      description: 'Header text for tier unlock requirement',
    },
    { tierName },
  )
  const headerText = isRetain ? toRetainText : toUnlockText

  const requirementsScenario = hasCredits && hasNights ? 'creditsAndNights' : hasCredits ? 'creditsOnly' : 'nightsOnly'
  const expirySelectValue = expiryDate ? 'true' : 'other'
  const formattedExpiryDate = expiryDate ? <FormatDateTime value={expiryDate} format="dmy-casual-short" /> : ''
  const boldTag = (chunks: React.ReactNode) => <BoldText>{chunks}</BoldText>

  return (
    <CardContainer>
      <CardHeader className={`tier-${tier}`}>
        <HeaderText className={`tier-${tier}`}>
          {headerText}
        </HeaderText>
      </CardHeader>
      <CardBody>
        <BodyTextStyled>
          <FormattedMessage
            id="2MANjODVnw"
            defaultMessage="You need {scenario, select, creditsAndNights {<bold>{creditsCount, plural, one {# Status Credit} other {# Status Credits}}</bold> and <bold>{nightsCount, plural, one {# night} other {# nights}}</bold>} creditsOnly {<bold>{creditsCount, plural, one {# Status Credit} other {# Status Credits}}</bold>} other {<bold>{nightsCount, plural, one {# night} other {# nights}}</bold>}}{hasExpiry, select, true { by {expiryDate}} other {}}."
            description="Body text showing status credits and nights needed for loyalty tier in the drawer card"
            values={{
              scenario: requirementsScenario,
              creditsCount: creditsRemaining,
              nightsCount: nightsRemaining,
              hasExpiry: expirySelectValue,
              expiryDate: formattedExpiryDate,
              bold: boldTag,
            }}
          />
        </BodyTextStyled>
      </CardBody>
    </CardContainer>
  )
}

export default LoyaltyDrawerTierCard
